<template>
  <div class="dk-page">
    <h2 class="section-title">Vedení ligy a DK</h2>

    <!-- Tabs -->
    <ul class="nav nav-tabs mb-4">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'league' }" @click="activeTab = 'league'" href="#">
          Vedení ligy
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'managers' }" @click="activeTab = 'managers'" href="#">
          Vedoucí týmů
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'members' }" @click="activeTab = 'members'" href="#">
          Členové DK
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'messages' }" @click="activeTab = 'messages'" href="#">
          Zprávy DK
        </a>
      </li>
    </ul>

    <!-- League Management Tab -->
    <div v-if="activeTab === 'league'">
      <!-- Leadership row -->
      <div class="leadership-row">
        <div v-if="president" class="role-card role-card--president">
          <div class="role-card__label">Prezident</div>
          <div class="role-card__name">{{ president.first_name }} {{ president.last_name }}</div>
        </div>

        <div v-if="vicePresident" class="role-card role-card--vice">
          <div class="role-card__label">Viceprezident</div>
          <div class="role-card__name">{{ vicePresident.first_name }} {{ vicePresident.last_name }}</div>
        </div>

        <div v-if="founder" class="role-card role-card--founder">
          <div class="role-card__label">Zakladatel</div>
          <div class="role-card__name">{{ founder.first_name }} {{ founder.last_name }}</div>
        </div>
      </div>

      <!-- Members section -->
      <div v-if="otherManagement.filter(m => m.role !== 'founder').length" class="members-section">
        <div class="members-section__label">Členové rady</div>
        <div class="members-grid">
          <div v-for="member in otherManagement.filter(m => m.role !== 'founder')" :key="member.id" class="member-item">
            {{ member.first_name }} {{ member.last_name }}
          </div>
        </div>
      </div>

      <div v-if="!president && !vicePresident && otherManagement.length === 0" class="empty-state">
        Žádná data k zobrazení
      </div>
    </div>

    <!-- Team Managers Tab -->
    <div v-if="activeTab === 'managers'">
      <div class="person-cards-grid">
        <div v-for="manager in teamManagers" :key="manager.id" class="person-card">
          <div class="person-card__team">
            <img v-if="manager.team_logo" :src="getLogoUrl(manager.team_logo)" :alt="manager.team_name + ' logo'" class="person-card__logo" />
            <span v-else class="person-card__logo-placeholder"></span>
            <span class="person-card__team-name">{{ manager.team_name }}</span>
          </div>
          <div class="person-card__name">{{ manager.first_name }} {{ manager.last_name }}</div>
        </div>
      </div>
    </div>

    <!-- DK Members Tab -->
    <div v-if="activeTab === 'members'">
      <div class="person-cards-grid">
        <div v-for="member in dkMembers" :key="member.id" class="person-card">
          <div class="person-card__team">
            <img v-if="member.team_logo" :src="getLogoUrl(member.team_logo)" :alt="member.team_name + ' logo'" class="person-card__logo" />
            <span v-else class="person-card__logo-placeholder"></span>
            <span class="person-card__team-name">{{ member.team_name }}</span>
          </div>
          <div class="person-card__name">{{ member.first_name }} {{ member.last_name }}</div>
        </div>
      </div>
    </div>

    <!-- DK Messages Tab -->
    <div v-if="activeTab === 'messages'" class="dk-messages">
      <div v-for="message in dkMessages" :key="message.id" class="dk-message card mb-3">
        <div class="card-body">
          <h5 class="card-title">{{ message.title }}</h5>
          <div class="dk-message-date">{{ formatDate(message.created_at) }}</div>
          <p class="card-text">{{ message.message }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DisciplinaryCommitteeView',
  data() {
    return {
      activeTab: 'league',
      dkMembers: [],
      dkMessages: [],
      leagueManagement: [],
      teamManagers: []
    }
  },
  computed: {
    president() {
      return this.leagueManagement.find(m => m.role === 'president');
    },
    vicePresident() {
      return this.leagueManagement.find(m => m.role === 'vice_president');
    },
    founder() {
      return this.leagueManagement.find(m => m.role === 'founder');
    },
    otherManagement() {
      return this.leagueManagement.filter(m => 
        m.role !== 'president' && m.role !== 'vice_president'
      );
    }
  },
  methods: {
    async loadData() {
      try {
        const [membersRes, messagesRes, managementRes, managersRes] = await Promise.all([
          axios.get('/api/dk/members'),
          axios.get('/api/dk/messages'),
          axios.get('/api/league-management'),
          axios.get('/api/team-managers')
        ]);
        this.dkMembers = membersRes.data;
        this.dkMessages = messagesRes.data;
        this.leagueManagement = managementRes.data;
        this.teamManagers = managersRes.data;
      } catch (error) {
        console.error('Error loading data:', error);
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('cs-CZ');
    },
    getLogoUrl(logo) {
      if (!logo) return '';
      if (logo.startsWith('http')) return logo;
      return `${process.env.VUE_APP_API_URL}${logo}`;
    }
  },
  mounted() {
    this.loadData();
  }
}
</script>

<style scoped>
.dk-page {
  max-width: 1200px;
  margin: 0 auto;
  background: #0a1023;
  color: #e0e6f7;
  min-height: 100vh;
  padding: 2rem 1rem 3rem;
}

.section-title {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
}

.section-subtitle {
  color: #8892b0;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  margin-bottom: 1rem;
}

.management-card {
  background: #151d3a;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(31, 224, 255, 0.08);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.management-card:hover {
  border-color: rgba(31, 224, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(10, 16, 35, 0.4);
}

.management-card h4 {
  color: #1fe0ff;
  margin-bottom: 0.5rem;
}

.management-card .position {
  color: #e0e6f7;
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

.contact-info {
  color: #8892b0;
}

.contact-info p {
  margin-bottom: 0.3rem;
}

.contact-info i {
  margin-right: 0.5rem;
  color: #1fe0ff;
}

.table {
  background: #151d3a;
  color: #e0e6f7;
  border-radius: 0.75rem;
  overflow: hidden;
  margin-bottom: 1.5rem;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid rgba(31, 224, 255, 0.08);
}

.table thead th {
  background: rgba(31, 224, 255, 0.08);
  color: #1fe0ff;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: none;
  padding: 0.9rem 1.25rem;
  border-bottom: 1px solid rgba(31, 224, 255, 0.12);
}

.table td, .table th {
  border: none;
  padding: 0.85rem 1.25rem;
  vertical-align: middle;
}

.table td {
  border-bottom: 1px solid rgba(31, 224, 255, 0.08);
}

.table tbody tr:last-child td {
  border-bottom: none;
}

.table tbody tr {
  transition: background 0.2s ease;
}

.table tbody tr:nth-of-type(even) {
  background: rgba(31, 224, 255, 0.03);
}

.table tbody tr:hover {
  background: rgba(31, 224, 255, 0.07);
}

.nav-tabs {
  border-bottom: none;
  gap: 0.5rem;
  padding-bottom: 1rem;
  flex-wrap: wrap;
}

.nav-tabs .nav-link {
  color: #8892b0;
  background: transparent;
  border: 1px solid rgba(31, 224, 255, 0.13);
  border-radius: 999px;
  padding: 0.45rem 1.15rem;
  font-weight: 600;
  font-size: 0.88rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin-right: 0;
  white-space: nowrap;
}

.nav-tabs .nav-link:hover {
  color: #1fe0ff;
  border-color: rgba(31, 224, 255, 0.3);
  background: rgba(31, 224, 255, 0.05);
}

.nav-tabs .nav-link.active {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  color: #0a1023 !important;
  font-weight: 700;
  border: none;
  box-shadow: 0 4px 20px rgba(31, 224, 255, 0.25);
}

.team-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(31, 224, 255, 0.1);
}

.team-logo-placeholder {
  width: 28px;
  height: 28px;
  background: rgba(136, 146, 176, 0.15);
  border-radius: 6px;
}

.dk-message {
  background: #151d3a;
  border: 1px solid rgba(31, 224, 255, 0.08);
  border-radius: 0.75rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dk-message:hover {
  border-color: rgba(31, 224, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(10, 16, 35, 0.4);
}

.dk-message .card-body {
  padding: 1.5rem;
}

.dk-message-date {
  color: #8892b0;
  font-size: 0.9em;
  margin-bottom: 0.5em;
}

.dk-message .card-title {
  color: #e0e6f7;
  font-weight: 700;
}

.dk-message .card-text {
  color: #e0e6f7;
}

.league-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  padding: 2rem;
  max-width: 600px;
  margin: 0 auto;
}

.grid-member {
  background: #151d3a;
  border: 1px solid rgba(31, 224, 255, 0.08);
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.grid-member:hover {
  border-color: rgba(31, 224, 255, 0.15);
  transform: translateY(-2px);
}

/* ── Leadership Row ── */
.leadership-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

@media (max-width: 768px) {
  .leadership-row {
    grid-template-columns: 1fr;
  }
}

.role-card {
  background: #151d3a;
  border: 1px solid rgba(31, 224, 255, 0.08);
  border-radius: 0.75rem;
  padding: 1.5rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.role-card:hover {
  transform: translateY(-3px);
  border-color: rgba(31, 224, 255, 0.18);
  box-shadow: 0 8px 24px rgba(10, 16, 35, 0.4);
}

.role-card__label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.75rem;
}

.role-card__name {
  font-size: 1.15rem;
  font-weight: 600;
  color: #e0e6f7;
}

.role-card--president {
  border-color: rgba(31, 224, 255, 0.2);
}

.role-card--president .role-card__label {
  color: #1fe0ff;
}

.role-card--vice {
  border-color: rgba(10, 124, 255, 0.2);
}

.role-card--vice .role-card__label {
  color: #5ea3ff;
}

.role-card--founder {
  border-color: rgba(52, 211, 153, 0.18);
}

.role-card--founder .role-card__label {
  color: #34d399;
}

/* ── Members Section ── */
.members-section {
  background: #151d3a;
  border: 1px solid rgba(136, 146, 176, 0.12);
  border-radius: 0.75rem;
  padding: 1.5rem;
}

.members-section__label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8892b0;
  margin-bottom: 1rem;
}

.members-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem 1.5rem;
}

@media (max-width: 992px) {
  .members-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .members-grid {
    grid-template-columns: 1fr;
  }
}

.member-item {
  font-size: 1rem;
  font-weight: 500;
  color: #e0e6f7;
  padding: 0.6rem 0.75rem;
  border-radius: 0.4rem;
  transition: background 0.2s ease;
}

.member-item:hover {
  background: rgba(31, 224, 255, 0.04);
}

/* ── Person Cards (Managers / DK Members) ── */
.person-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
}

@media (max-width: 992px) {
  .person-cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .person-cards-grid {
    grid-template-columns: 1fr;
  }
}

.person-card {
  background: #151d3a;
  border: 1px solid rgba(31, 224, 255, 0.08);
  border-radius: 0.75rem;
  padding: 1.25rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.person-card:hover {
  transform: translateY(-3px);
  border-color: rgba(31, 224, 255, 0.18);
  box-shadow: 0 8px 24px rgba(10, 16, 35, 0.4);
}

.person-card__team {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(31, 224, 255, 0.06);
}

.person-card__logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(31, 224, 255, 0.1);
  flex-shrink: 0;
}

.person-card__logo-placeholder {
  width: 32px;
  height: 32px;
  background: rgba(136, 146, 176, 0.15);
  border-radius: 4px;
  flex-shrink: 0;
}

.person-card__team-name {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8892b0;
}

.person-card__name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #e0e6f7;
}

.empty-state {
  text-align: center;
  color: #8892b0;
  padding: 3rem 1rem;
  font-style: italic;
}

.role-badge {
  display: inline-block;
  font-weight: 700;
  color: #1fe0ff;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(31, 224, 255, 0.1);
  border: 1px solid rgba(31, 224, 255, 0.18);
  padding: 0.35rem 0.85rem;
  border-radius: 0.4rem;
}

.name {
  font-size: 1.1rem;
  color: #e0e6f7;
  font-weight: 600;
  background: rgba(31, 224, 255, 0.04);
  border: 1px solid rgba(31, 224, 255, 0.06);
  border-radius: 0.5rem;
  padding: 0.7rem 0.5rem;
  margin-bottom: 0.2rem;
  display: inline-block;
  min-width: 120px;
}

.missing {
  color: #8892b0;
  font-style: italic;
}

.management-card, .management-section { display: none; }

.team-info {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.3rem;
  gap: 0.5rem;
}

.team-name {
  color: #1fe0ff;
  font-size: 1.05rem;
  font-weight: 500;
}
</style> 